/**
 * The capability console and the consent surface.
 *
 * The console is the hero of the interface: it shows exactly what the agent can
 * do right now, and it is rendered from the gate's snapshot so it cannot claim a
 * capability the browser has not registered.
 */

import type { CapabilityState, GateSnapshot } from '../lib/capability-gate';
import { CLAIMS, CLAIM_IDS, toolNameFor, type ClaimId } from '../domain/claims';
import { DRAFT_TOOL } from '../capabilities';
import { button, el, replaceChildren, secondsUntil } from './dom';

export interface ConsentActions {
  grant: (tool: string, reason: string) => Promise<void>;
  revoke: (tool: string) => void;
  deny: (tool: string) => void;
}

/** Default window for a grant made from the UI. */
export const GRANT_TTL_MS = 60_000;

/* ------------------------------------------------------------------ *
 * Capability console
 * ------------------------------------------------------------------ */

function capabilityRow(capability: CapabilityState): HTMLElement {
  const kind = capability.persistent
    ? 'always on'
    : capability.readOnly
      ? 'read'
      : 'write';

  const children = [
    el('span', { class: 'dot', attrs: { 'aria-hidden': 'true' } }),
    el('span', { class: 'name', text: capability.name }),
    el('span', { class: 'kind', text: kind }),
  ];

  // Hierarchy: persistent tools are structural context, so they show name and
  // tag only, with the full description available on hover. Consent-granted
  // tools are the point of the demo, so they show their description and status
  // inline and get visual promotion via the data-tier attribute.
  if (!capability.persistent) {
    children.push(el('span', { class: 'desc', text: capability.description }));
  } else {
    // Keep the detail reachable without spending vertical space on it.
    children[0]!.setAttribute('title', capability.description);
  }

  let urgency: 'calm' | 'soon' | 'imminent' = 'calm';
  if (capability.expiresAt !== null) {
    const seconds = secondsUntil(capability.expiresAt);
    urgency = seconds <= 5 ? 'imminent' : seconds <= 15 ? 'soon' : 'calm';
    children.push(
      el('span', {
        class: 'expiry',
        text: seconds > 0 ? `expires in ${seconds}s` : 'expiring',
      }),
    );
  }

  if (capability.callCount > 0) {
    children.push(
      el('span', {
        class: 'calls',
        text: capability.callCount === 1 ? 'called once' : `called ${capability.callCount} times`,
      }),
    );
  }

  return el(
    'div',
    {
      class: 'capability',
      data: {
        persistent: String(capability.persistent),
        expiring: String(capability.expiresAt !== null),
        tier: capability.persistent ? 'structural' : 'granted',
        urgency,
      },
    },
    children,
  );
}

export function renderConsole(host: HTMLElement | null, snapshot: GateSnapshot): void {
  if (!host) return;
  const granted = snapshot.capabilities.filter((capability) => capability.granted);
  // Consented capabilities first: they are the interesting ones.
  granted.sort((a, b) => Number(a.persistent) - Number(b.persistent) || a.name.localeCompare(b.name));
  replaceChildren(host, ...granted.map(capabilityRow));
}

/**
 * Live count of tools the agent can currently call. Rendered from the gate
 * snapshot so it moves the instant a grant or revocation changes the registry.
 * This is the number that dramatizes the whole thesis: the registry is state.
 */
export function renderConsoleCount(host: HTMLElement | null, snapshot: GateSnapshot): void {
  if (!host) return;
  const granted = snapshot.capabilities.filter((c) => c.granted).length;
  const consented = snapshot.capabilities.filter((c) => c.granted && !c.persistent).length;
  replaceChildren(
    host,
    el('span', { class: 'tool-count-n', text: String(granted) }),
    el('span', {
      text:
        granted === 1
          ? ' tool callable'
          : ` tools callable${consented > 0 ? `, ${consented} by your consent` : ''}`,
    }),
  );
}

/**
 * A raw value the wallet holds, and the getter tool an over-parameterized
 * design would have exposed to read it. The proof is derived from these: for
 * every sensitive field the app actually stores, show that no tool exists to
 * return it.
 */
export interface ForbiddenGetter {
  /** The tool name a naive design would register to read this field. */
  tool: string;
  /** The field it would expose, named as the person would recognise it. */
  field: string;
}

/**
 * The inspectable proof of the adversarial beat. Rather than assert "the agent
 * can't get your income", take the raw fields this wallet holds, and for each
 * one check the getter tool that would read it against the browser's own live
 * registry. Showing none of them exist demonstrates the guarantee from ground
 * truth, and ties it to the real data at risk rather than to a fixed list of
 * names. Each app passes the getters for the fields it actually stores, so the
 * same component stays honest across different domains.
 */
export function renderCannot(
  host: HTMLElement | null,
  liveToolNames: string[],
  forbidden: readonly ForbiddenGetter[],
): void {
  if (!host) return;
  const present = forbidden.filter((getter) => liveToolNames.includes(getter.tool));

  replaceChildren(
    host,
    el('p', { class: 'cannot-head', text: 'What the agent cannot ask for' }),
    el(
      'ul',
      { class: 'cannot-list' },
      forbidden.map((getter) =>
        el('li', { data: { present: String(present.some((p) => p.tool === getter.tool)) } }, [
          el('span', { class: 'cannot-mark', attrs: { 'aria-hidden': 'true' }, text: '\u2717' }),
          el('code', { text: getter.tool }),
          el('span', { class: 'cannot-field', text: getter.field }),
          el('span', { class: 'sr-only', text: ' is not registered' }),
        ]),
      ),
    ),
    el('p', {
      class: 'cannot-note',
      text:
        present.length === 0
          ? 'For every value this wallet holds, the tool that would read it is absent from the registry, so there is nothing for the agent to call. The absence is the mechanism.'
          : 'A raw-value tool is present. This should never happen.',
    }),
  );
}

/* ------------------------------------------------------------------ *
 * Pending consent requests, raised by the agent
 * ------------------------------------------------------------------ */

export function renderPending(
  host: HTMLElement | null,
  snapshot: GateSnapshot,
  actions: ConsentActions,
): void {
  if (!host) return;

  if (snapshot.pending.length === 0) {
    replaceChildren(host);
    host.hidden = true;
    return;
  }
  host.hidden = false;

  const rows = snapshot.pending.map((request) => {
    const claimId = CLAIM_IDS.find((id) => toolNameFor(id) === request.name);
    const spec = claimId ? CLAIMS[claimId] : undefined;

    return el('div', { class: 'request' }, [
      el('p', { class: 'request-title', text: spec ? spec.label : request.name }),
      el('p', { class: 'request-reason' }, [
        el('span', { class: 'quiet', text: 'The agent says: ' }),
        // Agent-authored text. Set as a text node, never parsed as markup.
        el('q', { text: request.reason }),
      ]),
      // Flag the provenance: this line is untrusted content authored by the
      // agent (untrustedContentHint), not a claim the site is making.
      el('p', { class: 'request-untrusted', attrs: { role: 'note' } }, [
        el('span', { class: 'untrusted-badge', text: 'agent-authored' }),
        el('span', { class: 'quiet', text: ' — treat this wording as untrusted; grant on the claim, not the phrasing.' }),
      ]),
      spec ? el('p', { class: 'request-discloses', text: spec.discloses }) : null,
      el('div', { class: 'request-actions' }, [
        button(
          `Permit for ${GRANT_TTL_MS / 1000}s`,
          () => actions.grant(request.name, request.reason),
          { variant: 'grant' },
        ),
        button('Decline', () => actions.deny(request.name), { variant: 'revoke' }),
      ]),
    ]);
  });

  replaceChildren(host, ...rows);
}

/* ------------------------------------------------------------------ *
 * Claim-by-claim consent controls
 * ------------------------------------------------------------------ */

function claimControl(
  claimId: ClaimId,
  snapshot: GateSnapshot,
  actions: ConsentActions,
): HTMLElement {
  const spec = CLAIMS[claimId];
  const tool = toolNameFor(claimId);
  const state = snapshot.capabilities.find((capability) => capability.name === tool);
  const granted = state?.granted === true;

  const meta: HTMLElement[] = [
    el('p', { class: 'claim-label', text: spec.label }),
    el('p', { class: 'claim-discloses', text: spec.discloses }),
  ];

  if (granted && state?.expiresAt != null) {
    const seconds = secondsUntil(state.expiresAt);
    meta.push(el('p', { class: 'claim-expiry', text: `permitted, ${seconds}s remaining` }));
  }

  return el('div', { class: 'claim', data: { granted: String(granted) } }, [
    el('div', { class: 'claim-meta' }, meta),
    granted
      ? button('Withdraw', () => actions.revoke(tool), { variant: 'revoke' })
      : button('Permit', () => actions.grant(tool, 'permitted directly from the console'), {
          variant: 'grant',
        }),
  ]);
}

export function renderClaimControls(
  host: HTMLElement | null,
  snapshot: GateSnapshot,
  actions: ConsentActions,
): void {
  if (!host) return;

  const claims = CLAIM_IDS.map((claimId) => claimControl(claimId, snapshot, actions));

  const draftState = snapshot.capabilities.find((capability) => capability.name === DRAFT_TOOL);
  const draftGranted = draftState?.granted === true;

  const draft = el('div', { class: 'claim claim-write', data: { granted: String(draftGranted) } }, [
    el('div', { class: 'claim-meta' }, [
      el('p', { class: 'claim-label', text: 'Draft an application' }),
      el('p', {
        class: 'claim-discloses',
        text: 'Lets the agent assemble an application citing verified claims. Writes, so it is permitted separately.',
      }),
    ]),
    draftGranted
      ? button('Withdraw', () => actions.revoke(DRAFT_TOOL), { variant: 'revoke' })
      : button('Permit', () => actions.grant(DRAFT_TOOL, 'application drafting approved'), {
          variant: 'grant',
        }),
  ]);

  replaceChildren(host, ...claims, draft);
}
